import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Form, Button, Alert, Spinner, Badge, Modal } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import TaskSubmissionModal from '../components/TaskSubmissionModal';
import axios from 'axios';

interface Task {
  _id: string;
  title: string;
  description: string;
  category: string;
  rewardAmount: number;
  requirements: string[];
  instructions: string;
  status: string;
  priority: string;
  maxSubmissions: number;
  currentSubmissions: number;
  estimatedTimeMinutes: number;
  expiryDate: string;
  allowedUserTypes: string[];
  views: number;
  completionRate: number;
  createdBy: {
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
  remainingSubmissions: number;
  isAvailable: boolean;
}

const UserTasks: React.FC = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  
  // Filters
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('priority');
  
  // Task details modal
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  
  // Task submission
  
  // Task submission modal
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [taskToSubmit, setTaskToSubmit] = useState<Task | null>(null);
  
  // User submissions
  const [userSubmissions, setUserSubmissions] = useState<any[]>([]);


  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  const categories = [
    { value: 'survey', label: 'Survey', icon: 'fas fa-poll' },
    { value: 'social_media', label: 'Social Media', icon: 'fas fa-share-alt' },
    { value: 'content_creation', label: 'Content Creation', icon: 'fas fa-pen-fancy' },
    { value: 'data_entry', label: 'Data Entry', icon: 'fas fa-keyboard' },
    { value: 'testing', label: 'Testing', icon: 'fas fa-bug' },
    { value: 'review', label: 'Review', icon: 'fas fa-star' },
    { value: 'other', label: 'Other', icon: 'fas fa-tasks' }
  ];

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(`${API_BASE_URL}/tasks`, {
        params: {
          userType: user?.userType || 'free'
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setTasks(response.data.tasks || []);
    } catch (error: any) {
      setError('Failed to load tasks. Please try again.');
      console.error('Load tasks error:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, user?.userType]);

  const loadUserSubmissions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/task-submissions/my-submissions`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setUserSubmissions(response.data.submissions || []);
    } catch (error: any) {
      console.error('Load user submissions error:', error);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    loadTasks();
    loadUserSubmissions();
  }, [loadTasks, loadUserSubmissions]);

  const openSubmissionModal = (task: Task) => {
    setMessage(''); // Clear any previous messages
    setTaskToSubmit(task);
    setShowSubmissionModal(true);
  };

  const handleSubmissionSuccess = () => {
    setMessage('Task submitted successfully! Your submission is being reviewed.');
    loadTasks();
    loadUserSubmissions();
  };

  const isTaskSubmitted = (taskId: string) => {
    return userSubmissions.some(submission => submission.taskId._id === taskId);
  };

  const getSubmissionStatus = (taskId: string) => {
    const submission = userSubmissions.find(s => s.taskId._id === taskId);
    return submission?.status || null;
  };

  const getCategoryInfo = (category: string) => {
    return categories.find(cat => cat.value === category) || categories[categories.length - 1];
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      low: 'secondary',
      medium: 'primary',
      high: 'warning',
      urgent: 'danger'
    };
    return colors[priority as keyof typeof colors] || 'secondary';
  };

  const getDifficultyLevel = (timeMinutes: number) => {
    if (timeMinutes <= 15) return { label: 'Easy', color: 'success' };
    if (timeMinutes <= 60) return { label: 'Medium', color: 'warning' };
    return { label: 'Hard', color: 'danger' };
  };

  const filteredAndSortedTasks = tasks
    .filter(task => {
      const matchesCategory = categoryFilter === 'all' || task.category === categoryFilter;
      const isAvailable = task.status === 'active' && new Date(task.expiryDate) > new Date();
      return matchesCategory && isAvailable;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'reward':
          return b.rewardAmount - a.rewardAmount;
        case 'time':
          return a.estimatedTimeMinutes - b.estimatedTimeMinutes;
        case 'expiry':
          return new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime();
        case 'priority':
        default:
          const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
          return (priorityOrder[b.priority as keyof typeof priorityOrder] || 0) - 
                 (priorityOrder[a.priority as keyof typeof priorityOrder] || 0);
      }
    });

  const openTaskDetails = (task: Task) => {
    setMessage(''); // Clear any previous messages
    setSelectedTask(task);
    setShowDetailsModal(true);
  };

  const getTimeRemaining = (expiryDate: string) => {
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return { text: 'Expired', color: 'danger' };
    if (diffDays === 1) return { text: '1 day left', color: 'warning' };
    if (diffDays <= 3) return { text: `${diffDays} days left`, color: 'warning' };
    return { text: `${diffDays} days left`, color: 'success' };
  };

  return (
    <div>
      <Row className="mb-4">
        <Col>
          <h1><i className="fas fa-tasks me-2"></i>Available Tasks</h1>
          <p className="text-muted">
            Complete tasks to earn rewards. Choose tasks that match your skills and interests.
          </p>
        </Col>
      </Row>

      {message && (
        <Row className="mb-4">
          <Col>
            <Alert variant="success" dismissible onClose={() => setMessage('')}>
              <i className="fas fa-check-circle me-2"></i>
              {message}
            </Alert>
          </Col>
        </Row>
      )}

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* User Type Badge */}
      {user && (
        <Row className="mb-3">
          <Col>
            <div className="d-flex align-items-center">
              <span className="me-2">Your Access Level:</span>
              <Badge bg={user.userType === 'admin' ? 'danger' : user.userType === 'executive' ? 'warning' : 'success'}>
                <i className="fas fa-user me-1"></i>
                {(user.userType || 'FREE').toUpperCase()} User
              </Badge>
              {(user.userType || 'free') === 'free' && (
                <small className="text-muted ms-2">
                  Upgrade to Executive for access to premium tasks!
                </small>
              )}
            </div>
          </Col>
        </Row>
      )}

      {/* Filters and Sort */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Filter by Category</Form.Label>
                <Form.Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Sort By</Form.Label>
                <Form.Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="priority">Priority</option>
                  <option value="reward">Reward Amount</option>
                  <option value="time">Time Required</option>
                  <option value="expiry">Expiry Date</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex align-items-end">
              <Button variant="outline-secondary" onClick={() => {
                setCategoryFilter('all');
                setSortBy('priority');
              }}>
                <i className="fas fa-refresh me-2"></i>
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tasks Grid */}
      {loading ? (
        <div className="text-center p-4">
          <Spinner animation="border" />
          <p className="mt-2">Loading available tasks...</p>
        </div>
      ) : (
        <Row>
          {filteredAndSortedTasks.map(task => {
            const categoryInfo = getCategoryInfo(task.category);
            const difficulty = getDifficultyLevel(task.estimatedTimeMinutes);
            const timeRemaining = getTimeRemaining(task.expiryDate);
            
            return (
              <Col key={task._id} lg={6} xl={4} className="mb-4">
                <Card className="h-100 task-card">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <i className={`${categoryInfo.icon} me-2`}></i>
                      <Badge bg="info">{categoryInfo.label}</Badge>
                    </div>
                    <Badge bg={getPriorityColor(task.priority)}>
                      {task.priority.toUpperCase()}
                    </Badge>
                  </Card.Header>
                  
                  <Card.Body className="d-flex flex-column">
                    <h5 className="card-title">{task.title}</h5>
                    <p className="card-text text-muted flex-grow-1">
                      {task.description.length > 120 
                        ? `${task.description.substring(0, 120)}...` 
                        : task.description
                      }
                    </p>
                    
                    <div className="mt-auto">
                      <Row className="mb-3">
                        <Col xs={6}>
                          <div className="text-center">
                            <div className="h4 text-success mb-0">₹{task.rewardAmount}</div>
                            <small className="text-muted">Reward</small>
                          </div>
                        </Col>
                        <Col xs={6}>
                          <div className="text-center">
                            <div className="h6 mb-0">
                              <i className="fas fa-clock me-1"></i>
                              {task.estimatedTimeMinutes} min
                            </div>
                            <Badge bg={difficulty.color} className="small">
                              {difficulty.label}
                            </Badge>
                          </div>
                        </Col>
                      </Row>
                      
                      <div className="mb-3">
                        <div className="d-flex justify-content-between small text-muted mb-1">
                          <span>Submissions: {task.currentSubmissions}/{task.maxSubmissions}</span>
                          <Badge bg={timeRemaining.color} className="small">
                            {timeRemaining.text}
                          </Badge>
                        </div>
                        <div className="progress" style={{ height: '4px' }}>
                          <div 
                            className="progress-bar" 
                            style={{ 
                              width: `${(task.currentSubmissions / task.maxSubmissions) * 100}%` 
                            }}
                          ></div>
                        </div>
                      </div>
                      
                      <div className="d-grid gap-2">
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => openTaskDetails(task)}
                        >
                          <i className="fas fa-info-circle me-2"></i>
                          View Details
                        </Button>
                        {isTaskSubmitted(task._id) ? (
                          <Button variant="info" size="sm" disabled>
                            <i className="fas fa-check me-2"></i>
                            {getSubmissionStatus(task._id) === 'approved' ? 'Approved' :
                             getSubmissionStatus(task._id) === 'rejected' ? 'Rejected' :
                             getSubmissionStatus(task._id) === 'under_review' ? 'Under Review' :
                             'Submitted'}
                          </Button>
                        ) : (
                          <Button 
                            variant="success"
                            size="sm"
                            onClick={() => openSubmissionModal(task)}
                            disabled={
                              task.currentSubmissions >= task.maxSubmissions ||
                              new Date(task.expiryDate) <= new Date()
                            }
                          >
                            <i className="fas fa-upload me-2"></i>
                            Submit Task
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {!loading && filteredAndSortedTasks.length === 0 && (
        <div className="text-center p-5">
          <i className="fas fa-tasks text-muted" style={{ fontSize: '4rem' }}></i>
          <h4 className="text-muted mt-3">No tasks available</h4>
          <p className="text-muted">
            {categoryFilter !== 'all' 
              ? 'Try changing your category filter to see more tasks.'
              : 'Check back later for new tasks, or contact support if you think this is an error.'
            }
          </p>
          {(user?.userType || 'free') === 'free' && (
            <div className="mt-3">
              <Button variant="primary" href="/app/upgrade">
                <i className="fas fa-arrow-up me-2"></i>
                Upgrade to Executive for More Tasks
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Task Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedTask && (
              <>
                <i className={`${getCategoryInfo(selectedTask.category).icon} me-2`}></i>
                {selectedTask.title}
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTask && (
            <>
              <Row className="mb-4">
                <Col md={8}>
                  <h6>Description</h6>
                  <p>{selectedTask.description}</p>
                  
                  <h6>Instructions</h6>
                  <p>{selectedTask.instructions}</p>
                  
                  <h6>Requirements</h6>
                  <ul>
                    {selectedTask.requirements.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </Col>
                <Col md={4}>
                  <Card bg="light">
                    <Card.Body>
                      <h6>Task Details</h6>
                      <div className="mb-2">
                        <strong>Reward:</strong> ₹{selectedTask.rewardAmount}
                      </div>
                      <div className="mb-2">
                        <strong>Estimated Time:</strong> {selectedTask.estimatedTimeMinutes} minutes
                      </div>
                      <div className="mb-2">
                        <strong>Difficulty:</strong>{' '}
                        <Badge bg={getDifficultyLevel(selectedTask.estimatedTimeMinutes).color}>
                          {getDifficultyLevel(selectedTask.estimatedTimeMinutes).label}
                        </Badge>
                      </div>
                      <div className="mb-2">
                        <strong>Priority:</strong>{' '}
                        <Badge bg={getPriorityColor(selectedTask.priority)}>
                          {selectedTask.priority.toUpperCase()}
                        </Badge>
                      </div>
                      <div className="mb-2">
                        <strong>Submissions:</strong> {selectedTask.currentSubmissions}/{selectedTask.maxSubmissions}
                      </div>
                      <div className="mb-3">
                        <strong>Expires:</strong>{' '}
                        <Badge bg={getTimeRemaining(selectedTask.expiryDate).color}>
                          {getTimeRemaining(selectedTask.expiryDate).text}
                        </Badge>
                      </div>
                      
                      {selectedTask.completionRate > 0 && (
                        <div className="mb-2">
                          <strong>Success Rate:</strong> {selectedTask.completionRate}%
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
          {selectedTask && (
            isTaskSubmitted(selectedTask._id) ? (
              <Button variant="info" disabled>
                <i className="fas fa-check me-2"></i>
                {getSubmissionStatus(selectedTask._id) === 'approved' ? 'Approved' :
                 getSubmissionStatus(selectedTask._id) === 'rejected' ? 'Rejected' :
                 getSubmissionStatus(selectedTask._id) === 'under_review' ? 'Under Review' :
                 'Submitted'}
              </Button>
            ) : (
              <Button 
                variant="success"
                onClick={() => {
                  setShowDetailsModal(false);
                  openSubmissionModal(selectedTask);
                }}
                disabled={
                  selectedTask.currentSubmissions >= selectedTask.maxSubmissions ||
                  new Date(selectedTask.expiryDate) <= new Date()
                }
              >
                <i className="fas fa-upload me-2"></i>
                Submit This Task
              </Button>
            )
          )}
        </Modal.Footer>
      </Modal>

      {/* Task Submission Modal */}
      <TaskSubmissionModal
        show={showSubmissionModal}
        onHide={() => setShowSubmissionModal(false)}
        task={taskToSubmit}
        onSubmissionSuccess={handleSubmissionSuccess}
      />

      <style>{`
        .task-card {
          transition: transform 0.2s, box-shadow 0.2s;
          border: 1px solid #dee2e6;
        }
        .task-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
      `}</style>
    </div>
  );
};

export default UserTasks;